import React from 'react'
import Head from 'next/head'
import axios from 'axios'
import { BreadcrumbJsonLd, NextSeo } from 'next-seo'
import { wrapper } from '../store/configStore'
import { SERVER_IP } from '../store/middleware/api'
import TradeShowProductionPage from '../components/design-system/marketing/TradeShowProductionPage'
import { tradeShowProductionFallback } from '../content/seo/tradeShowProduction'
import { compactSeoPublicPayload } from '../utilities/seoPublicPayload'

const getTradeShowSeoData = async ({ preview, previewData }) => {
  try {
    const response = await axios.get(`${SERVER_IP}/seo/pages/${preview ? 'preview' : 'published'}`, {
      params: preview
        ? {
            path: tradeShowProductionFallback.page.path,
            token: previewData?.seoPreviewToken
          }
        : { path: tradeShowProductionFallback.page.path }
    })

    return compactSeoPublicPayload(response.data.data, {
      source: preview ? 'draft-preview' : 'published',
      customKeys: ['tradeShowProduction']
    })
  } catch (error) {
    console.warn(`Using source fallback for ${tradeShowProductionFallback.page.path}: ${error.message}`)
    return compactSeoPublicPayload(tradeShowProductionFallback, {
      source: 'source-fallback',
      customKeys: ['tradeShowProduction']
    })
  }
}

export const getStaticProps = wrapper.getStaticProps(
  async ({ preview, previewData }) => {
    const [reviewResponse, seoData] = await Promise.all([
      axios.get(`${SERVER_IP}/google-review/public`, {
        params: {
          page: tradeShowProductionFallback.page.path,
          limit: 3
        }
      }).catch(error => {
        console.warn(`Using review fallback for ${tradeShowProductionFallback.page.path}: ${error.message}`)
        return { data: null }
      }),
      getTradeShowSeoData({ preview, previewData })
    ])

    return {
      props: {
        reviewData: reviewResponse.data,
        seoData,
        isPreview: Boolean(preview)
      },
      revalidate: 3600
    }
  }
)

const mergeTradeShowContent = stored => {
  const fallback = tradeShowProductionFallback.revision.content.custom.tradeShowProduction
  const next = stored?.custom?.tradeShowProduction || {}
  const mergeObject = key => ({ ...fallback[key], ...(next[key] || {}) })

  return {
    ...fallback,
    ...next,
    hero: mergeObject('hero'),
    proof: next.proof?.length ? next.proof : fallback.proof,
    paths: {
      ...mergeObject('paths'),
      managed: { ...fallback.paths.managed, ...(next.paths?.managed || {}) },
      equipment: { ...fallback.paths.equipment, ...(next.paths?.equipment || {}) }
    },
    formats: {
      ...mergeObject('formats'),
      items: next.formats?.items?.length ? next.formats.items : fallback.formats.items
    },
    gallery: {
      ...mergeObject('gallery'),
      items: next.gallery?.items?.length ? next.gallery.items : fallback.gallery.items
    },
    capabilities: {
      ...mergeObject('capabilities'),
      items: next.capabilities?.items?.length ? next.capabilities.items : fallback.capabilities.items
    },
    coordination: {
      ...mergeObject('coordination'),
      items: next.coordination?.items?.length ? next.coordination.items : fallback.coordination.items
    },
    process: {
      ...mergeObject('process'),
      steps: next.process?.steps?.length ? next.process.steps : fallback.process.steps
    },
    faqs: next.faqs?.length ? next.faqs : fallback.faqs,
    proposal: mergeObject('proposal'),
    related: {
      ...mergeObject('related'),
      items: next.related?.items?.length ? next.related.items : fallback.related.items
    }
  }
}

const TradeShowAvServices = ({ reviewData, seoData }) => {
  const page = seoData?.page || tradeShowProductionFallback.page
  const revision = seoData?.revision || tradeShowProductionFallback.revision
  const hasTradeShowContent = Boolean(revision.content?.custom?.tradeShowProduction)
  const seo = {
    ...tradeShowProductionFallback.revision.seo,
    ...(hasTradeShowContent ? (revision.seo || {}) : {})
  }
  const content = mergeTradeShowContent(revision.content)
  const canonical = seo.canonicalUrl || page.canonicalUrl
  const openGraph = {
    type: 'website',
    url: seo.openGraph?.url || canonical,
    title: seo.openGraph?.title || seo.title,
    description: seo.openGraph?.description || seo.description,
    images: seo.openGraph?.images || tradeShowProductionFallback.revision.seo.openGraph.images,
    site_name: 'Miami Sound Rental'
  }
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${canonical}#service`,
    name: 'Trade Show and Expo AV Services',
    serviceType: 'Trade show audiovisual production and exhibitor AV services',
    url: canonical,
    description: seo.description,
    provider: {
      '@id': 'https://miamisoundrental.com/'
    },
    audience: {
      '@type': 'BusinessAudience',
      audienceType: 'Exhibitors, agencies, booth builders, show organizers, sponsors, and brand teams'
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Trade show and expo audiovisual capabilities',
      itemListElement: [
        'LED video walls and displays',
        'Booth audio and microphones',
        'Exhibit lighting',
        'Content playback and switching',
        'Installation and dismantle labor',
        'Show-floor technical support and coordination'
      ].map(name => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name
        }
      }))
    },
    areaServed: [
      { '@type': 'City', name: 'Miami' },
      { '@type': 'City', name: 'Fort Lauderdale' },
      { '@type': 'City', name: 'Orlando' },
      { '@type': 'State', name: 'Florida' }
    ]
  }

  return (
    <>
      <NextSeo
        title={seo.title}
        description={seo.description}
        canonical={canonical}
        noindex={seo.robots?.index === false}
        nofollow={seo.robots?.follow === false}
        twitter={{
          handle: '@miamisoundrent1',
          site: '@miamisoundrent1',
          cardType: seo.twitter?.cardType || 'summary_large_image'
        }}
        openGraph={openGraph}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          { position: 1, name: 'Home', item: 'https://miamisoundrental.com/' },
          { position: 2, name: 'Corporate Event Production', item: 'https://miamisoundrental.com/corporate-event-production' },
          { position: 3, name: 'Trade Show & Expo AV Services', item: canonical }
        ]}
      />
      <Head>
        <script
          key='trade-show-production-service-schema'
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
        />
      </Head>
      <TradeShowProductionPage content={content} reviewData={reviewData} />
    </>
  )
}

export default TradeShowAvServices
